import { useCallback, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePlaidLink } from "react-plaid-link";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PLAID_OAUTH_STATE_KEY = "plaid_oauth_state";

const readOauthState = () => {
  try {
    const raw = sessionStorage.getItem(PLAID_OAUTH_STATE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const PlaidOauthReturn = () => {
  const navigate = useNavigate();
  const opened = useRef(false);
  const state = useMemo(() => readOauthState(), []);

  const onSuccess = useCallback(async (public_token) => {
    if (!state) return;

    try {
      await api.post(`/plaid/link/exchange?household_id=${state.hid}`, { public_token });
      sessionStorage.removeItem(PLAID_OAUTH_STATE_KEY);
      toast.success("Bank connection completed");
      navigate(state.resume_path || "/", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete bank connection");
    }
  }, [navigate, state]);

  const { open, ready } = usePlaidLink({
    token: state?.token || null,
    receivedRedirectUri: state ? window.location.href : undefined,
    onSuccess,
  });

  useEffect(() => {
    if (!state?.token || !ready || opened.current) return;
    opened.current = true;
    open();
  }, [open, ready, state]);

  if (!state?.token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-sm text-center">
          <p className="font-display text-lg font-bold">Bank connection could not resume</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Start the bank connection again from your household.
          </p>
          <Link to="/" className="mt-4 inline-block text-sm font-semibold text-primary">
            Return home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <p className="text-sm text-muted-foreground">Resuming bank connection...</p>
    </div>
  );
};
